import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { useCarCatalogue } from '../../application/cars/useCarCatalogue';
import { useCompareState } from '../../application/compare/compareState';
import useCarCollection from '../../application/game/useCarCollection';
import CompareAccelerationChart from '../compare/CompareAccelerationChart';
import CompareSpecTable from '../compare/CompareSpecTable';
import CompareValueChart from '../compare/CompareValueChart';
import ComparePicker from '../compare/ComparePicker';
import RaceStage from '../compare/RaceStage';
import { FlagIcon, WheelIcon } from '../icons/ControlIcons';

/**
 * The Duel (SPEC-008) — two cars, every figure, and a race down a strip.
 *
 * A composition with no rule in it, the way `GarageScreen` is a composition with
 * no state in it: the pair and the conditions are the URL, the physics is
 * `domain/race`, the table is `domain/compare`, and this file decides only what
 * order things are read in and what opens the race.
 *
 * THE RACE IS NOT ON THIS PAGE. It opens full screen (`RaceStage`), and the
 * reason is what it is: a race that shares a page with a specification table is
 * a chart among charts — the reader arrives at it mid-scroll, watches twelve
 * seconds of it with a spec table in the corner of their eye, and leaves. Taken
 * full screen it can be what it actually is. What is left here is everything a
 * reader READS rather than watches: who is racing, what two road tests actually
 * timed, what the two have been worth, and every published figure side by side.
 *
 * THIS SECTION IS THE DUEL and the streak game in the next tab is the
 * COMPETITION. They were named the other way round and the swap is the owner's,
 * but it is also the more honest pair of words: a duel is two things against
 * each other, which is this, and a competition is a run of rounds against a
 * board, which is that. Only the copy moved — `/duel` still addresses the
 * competition and `/compare` still addresses this, because the two would have to
 * swap in one step and `/duel` belongs to SPEC-007.
 *
 * Every state here is a designed one, for the reason the garage's and the
 * competition's are: this is a black page, and a blank one is indistinguishable
 * from a broken one.
 *
 * SINCE SPEC-010 THE PICKER OFFERS THE READER'S OWN CARS. A screen that would
 * put a car on a strip before the reader had been introduced to it would be
 * handing over the whole spec sheet of a card they are supposed to be playing
 * for. An address naming a locked car falls back to the default pair, which is
 * what `readPairFromSearch` already does for an id the catalogue cannot supply
 * — there is no second rule here, only a shorter list.
 */

export default function CompareScreen() {
    const t = useTranslate();
    const { cars: catalogue, status } = useCarCatalogue();
    const { unlocked } = useCarCollection({ cars: catalogue });
    const { cars, conditions, setCar, setCondition } = useCompareState(unlocked);
    const [isRacing, setRacing] = useState(false);

    if (status === 'loading') {
        return <CompareEmptyState message={t('compare.loading')} />;
    }

    if (status === 'failed') {
        return <CompareEmptyState message={t('compare.failed')} />;
    }

    // A catalogue with fewer than two cars in it. It is fetched, so this is a
    // state the screen reaches in the wild rather than an impossible one.
    if (cars.some((car) => !car)) {
        return <CompareEmptyState message={t('compare.unavailable')} />;
    }

    return (
        <section className="compare">
            <div className="visually-hidden">
                <h1>{t('compare.title')}</h1>
                <p>{t('compare.intro')}</p>
            </div>

            {/* The way in is INSIDE the picker, and above 1024 that is the
                whole composition: two cars at full bleed facing each other
                across the control that races them. On a phone the grid puts it
                on its own row underneath, which is exactly where it was — the
                markup moved so the desktop could exist, and the phone did not
                notice. */}
            <ComparePicker cars={cars} catalogue={unlocked} onChoose={setCar}>
                <section className="compare__challenge">
                    <p className="compare__challenge-blurb">{t('compare.stage.blurb')}</p>
                    <button
                        type="button"
                        className="button button--primary compare__challenge-action"
                        onClick={() => setRacing(true)}
                    >
                        <FlagIcon size={20} />
                        {t('compare.stage.open')}
                    </button>
                </section>
            </ComparePicker>

            {/* The three reading bands, in one region of their own so a wide
                screen can lay them out as columns without the confrontation
                above being dragged into the same grid. Each carries a modifier
                naming which column it takes above 1440 — the sheet cannot ask
                "which band is this?" of an element that only says `compare__band`,
                and `:nth-child` would tie the layout to the order rather than to
                the content. */}
            <div className="compare__reading">
                {/* FIRST among the bands, directly under the button that opens
                    the race, because it is the measured answer to the question
                    the race answers by simulation. A reader who watches twelve
                    seconds of physics and then scrolls should meet two
                    magazines' stopwatches before they meet anything else this
                    screen computed. */}
                <CompareBand titleKey="compare.section.acceleration" name="accel">
                    <CompareAccelerationChart cars={cars} />
                </CompareBand>

                <CompareBand titleKey="compare.section.value" name="value">
                    <CompareValueChart cars={cars} />
                </CompareBand>

                <CompareBand titleKey="compare.section.specs" name="specs">
                    <CompareSpecTable cars={cars} />
                </CompareBand>
            </div>

            {/* Mounted only while it is open, so `showModal()` runs once per
                opening and the stage cannot hold a stale pair in its props. */}
            {isRacing ? (
                <RaceStage
                    cars={cars}
                    conditions={conditions}
                    onCondition={setCondition}
                    onClose={() => setRacing(false)}
                />
            ) : null}
        </section>
    );
}

/** One band: a heading a reader can navigate by, and whatever it contains. */
function CompareBand({ children, name, titleKey }) {
    const t = useTranslate();

    return (
        <section className={`compare__band compare__band--${name}`}>
            <h2 className="compare__band-title">{t(titleKey)}</h2>
            {children}
        </section>
    );
}

function CompareEmptyState({ message }) {
    return (
        <section className="compare compare--empty">
            <div className="empty-state">
                <WheelIcon className="empty-state__mark" size={32} />
                <p className="empty-state__message">{message}</p>
            </div>
        </section>
    );
}
